import {IRichExternalLinkHandler} from "./rich.external-link-handler.interface";
import React from "react";
import {
    ExternalLinkInIframeHandlerBase,
    ExternalLinkInIframeRendererParams
} from "../external-link-in-iframe-handler-base";
import {GNWP_SOURCES} from "../../../gnwp/gnwp-consts";
import {ExternalLinksZIndexes} from "../../external-links-z-indexes";
import {CIPropRead} from "../../../../../utils/case-insensitive-property-reader";
import {CHANNEL_SOURCE} from "../../../../../common/const/casinofied-analytics-consts";
import {AreaType} from "@sparkware/uc-sdk-core";
import {OpenRichPageOptions} from "../../external-links.service.interface";
import {CyClass} from "../../../../../common/cy-classes";

const RICH_POST_ACTIONS = {
    OPEN_REAL_GAME: '1',
    OPEN_ANONYMOUS_GAME: '36',
    CASHIER: '6',
    SPORT: '24',
    EXTERNAL_URL: '27',
    REMOVE_DAILY_DEAL_INDICATION: '64',
};


export class RichExternalLinkHandler extends ExternalLinkInIframeHandlerBase implements IRichExternalLinkHandler {

    async openRichPage(options: OpenRichPageOptions): Promise<void> {
        if(!options.campaignId) {
            throw new Error('Rich campaignId parameter was not provided');
        }

        const url = this.services.configuration.servicesConfigs.Integration.rich?.richPageUrl;
        if(!url) {
            throw new Error('Rich page url was not configured');
        }

        await this.services.location.push(url.replace('{campaignid}', options.campaignId));
    }

    async renderRichPageContent(): Promise<React.ReactElement> {
        const url = await this._getGnwpUrl();
        return this._renderExternalLinkIframe(url);
    }
    protected async _getGnwpUrl(): Promise<string> {
        const campaignId = parseInt(this.services.location.currentUri.queryParams.getParamValue('campaignid'));
        return await this.services.gnwp.getRichPageUrl({
            marketingCampaignId: campaignId
        });
    }

    protected _getGnwpSource(): GNWP_SOURCES {
        return GNWP_SOURCES.RICH;
    }

    protected _getIframeRendererParams(): ExternalLinkInIframeRendererParams {
        return {
            maxWidth: 1008,
            maxHeight: 702,
            zIndex: ExternalLinksZIndexes.MyAccount,
            hideCloseButton: false,
            httpVerb: 'POST',
            analyticsAction: this._getGnwpSource(),
            iframeExtraCssClass: CyClass('cy-rich-external-link-container')
        };
    }

    protected async _onPostMessageReceived(message: MessageEvent): Promise<void> {
        const actionId = this._extractActionId(message);
        if(!actionId) {
            return;
        }

        switch (actionId) {
            case RICH_POST_ACTIONS.EXTERNAL_URL:
                await this._redirectToExternalUrl(CIPropRead.getStringProperty('Url', this._parseMessageData(message)));
                break;
            case RICH_POST_ACTIONS.CASHIER:
                await this._openCashier({
                    tabId: this._extractCashierTabId(message),
                    promCode: this._extractPromoCode(message),
                    channel: {
                        source: CHANNEL_SOURCE.rich,
                        area: AreaType.button
                    }
                });
                break;
            case RICH_POST_ACTIONS.OPEN_REAL_GAME:
                await this._openRealGame(CIPropRead.getNumericProperty('GameId', this._parseMessageData(message)));
                break;
            case RICH_POST_ACTIONS.OPEN_ANONYMOUS_GAME:
                await this._openDemoGame(CIPropRead.getNumericProperty('GameId', this._parseMessageData(message)));
                break;
            case RICH_POST_ACTIONS.SPORT:
                await this._openSport();
                break;
            case RICH_POST_ACTIONS.REMOVE_DAILY_DEAL_INDICATION:
                this.services.dailyDeal.removeDailyDealIndicator();
                break;

        }
    }

    protected async _redirectToExternalUrl(url: string) {
        await this.services.location.redirect(url)
    }

    protected async _openRealGame(gameType: number): Promise<void> {
        const gameInfo = await this.services.games.getGameInfo(gameType);
        if(!gameInfo) {
            this.services.logger.error(`RICH page is trying to open game ${gameType} in real mode but this game is not in the layout`);
            return;
        }

        await gameInfo.open({
            isRealMode: true
        });
    }

    protected async _openDemoGame(gameType: number): Promise<void> {
        const gameInfo = await this.services.games.getGameInfo(gameType);
        if(!gameInfo) {
            this.services.logger.error(`RICH page is trying to open game ${gameType} in demo mode but this game is not in the layout`);
            return;
        }

        await gameInfo.open({
            isRealMode: false
        });
    }

    protected async _openSport(): Promise<void> {
        await this.services.linksNavigation.openSport();
    }
}